import {
  Button,
  ButtonColor,
  CategorizationTagColor,
  CopyToClipboardButton,
  Field,
  Modal,
  ModalBody,
  ModalContent,
  ModalContentSize,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  optionWithTagProps,
  PathCompositionSize,
  SelectControl,
  TruncableText,
  useCloseModal
} from "@quortex/front-common"
import { useMatchesParams } from "@quortex/front-common/adapters/react-location"
import { type MakeGenerics, useSearch } from "@tanstack/react-location"
import { useSuspenseQuery } from "@tanstack/react-query"
import { QRCodeSVG } from "qrcode.react"
import { type FC, useState } from "react"
import { Controller, FormProvider, useForm } from "react-hook-form"
import PlayPathComposition from "@/App/play/components/features/endpoints/ottEndpoints/PlayPathComposition"
import { useOttEndpointDomainName, useOttEndpointUrl } from "@/App/play/helpers/ottendpoints"
import { useCdnList } from "@/queries/playCdns"
import { useInput } from "@/queries/playInputs"
import { useOttEndpoint } from "@/queries/playOttEndpoints"
import { usePool } from "@/queries/playPools"
import { useProcessing } from "@/queries/playProcessings"
import { useTarget } from "@/queries/playTargets"

export type ShareUrlModalGenerics = MakeGenerics<{
  Search: { modal: { name: "share_url"; ottEndpointUuid?: string } }
}>

const ShareUrlModal: FC = () => {
  const search = useSearch<ShareUrlModalGenerics>()
  const params = useMatchesParams()
  const closeModal = useCloseModal()
  const ottEndpointUuid = params.ottEndpointUuid || search.modal?.ottEndpointUuid

  return (
    <Modal show={search.modal?.name === "share_url"} onDismiss={closeModal}>
      <Content ottEndpointUuid={ottEndpointUuid} />
    </Modal>
  )
}

const Content: FC<{ ottEndpointUuid?: string }> = ({ ottEndpointUuid }) => {
  const closeModal = useCloseModal()
  const [stabilizedEndpointUuid] = useState(ottEndpointUuid) // to prevent 404 query error when modal is closing
  const { data: pool } = useSuspenseQuery(usePool())
  const { data: endpoint } = useSuspenseQuery(
    useOttEndpoint({ ottEndpointUuid: stabilizedEndpointUuid })
  )
  const { data: input } = useSuspenseQuery(useInput({ inputUuid: endpoint.inputUuid }))
  const { data: processing } = useSuspenseQuery(
    useProcessing({ processingUuid: endpoint.processingUuid })
  )
  const { data: target } = useSuspenseQuery(useTarget({ targetUuid: endpoint.targetUuid }))
  const { data: enabledCdnList } = useSuspenseQuery({
    ...useCdnList(),
    select: data => data?.results.filter(cdn => cdn.enabled)
  })
  const form = useForm({
    defaultValues: {
      cdnUuid: enabledCdnList.length === 1 ? enabledCdnList[0]?.uuid : undefined
    }
  })
  const selectedCdn = enabledCdnList.find(cdn => cdn.uuid === form.watch("cdnUuid"))
  const domainName = useOttEndpointDomainName(selectedCdn)
  const url = useOttEndpointUrl(endpoint, selectedCdn)

  return (
    <ModalContent size={ModalContentSize.XS}>
      <ModalHeader onClose={closeModal}>
        <ModalTitle>Share URL</ModalTitle>
      </ModalHeader>
      <ModalBody className="overflow-hidden">
        {enabledCdnList.length > 1 && (
          <FormProvider {...form}>
            <Field className="mb-md" label="CDN" name="cdnUuid">
              {name => (
                <Controller
                  control={form.control}
                  name={name}
                  render={({ field }) => (
                    <SelectControl
                      {...field}
                      fixedMenu
                      options={enabledCdnList.map(cdn => ({
                        label: cdn.name ?? "",
                        tagList:
                          cdn.type === "builtin"
                            ? [{ color: CategorizationTagColor.OUTLINE, label: "Built-in" }]
                            : [],
                        value: cdn.uuid
                      }))}
                      placeholder="Enter or select a CDN"
                      {...optionWithTagProps}
                    />
                  )}
                  rules={{
                    required: "The CDN is required"
                  }}
                />
              )}
            </Field>
          </FormProvider>
        )}
        <div className="relative flex h-49 flex-col items-center justify-center gap-sm">
          {selectedCdn ? (
            <>
              <QRCodeSVG className="h-30 w-30" value={url} />
              <div className="radius-xs flex w-full items-center justify-between gap-xs border border-grey-400 p-sm">
                <TruncableText
                  artificialEllipsis
                  ellipsisClassName="font-monospace text-xs text-grey-600"
                  withTooltip={false}
                >
                  <PlayPathComposition
                    disabled={endpoint.status === "disabled"}
                    dns={`https://${domainName}`}
                    endpoint={{
                      ...endpoint,
                      input,
                      processing,
                      target
                    }}
                    processingType={pool.type}
                    size={PathCompositionSize.XSMALL}
                    wrap={false}
                  />
                </TruncableText>
                <CopyToClipboardButton valueToCopy={url} />
              </div>
            </>
          ) : (
            <span className="text-grey-600 text-sm">
              Select a CDN to generate the full URL and QR code.
            </span>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color={ButtonColor.SECONDARY} type="button" onClick={closeModal}>
          Cancel
        </Button>
        <Button color={ButtonColor.PRIMARY} type="button" onClick={closeModal}>
          Done
        </Button>
      </ModalFooter>
    </ModalContent>
  )
}

export default ShareUrlModal
